/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiBox,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiX,
  FiTag,
  FiPackage,
  FiActivity,
  FiCheck,
  FiHelpCircle,
} from "react-icons/fi";
import Swal from "sweetalert2";
import PageContainer from "../components/PageContainer";
import UnifiedCard from "../components/UnifiedCard";
import ResponsiveTable from "../components/ResponsiveTable";
import ProductFormFields from "../components/ProductFormFields";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import {
  MAX_MAIN_IMAGES,
  emptyMainImageSlot,
  extractMainImageUrls,
  getFormMainImageSlots,
  slotsFromImageUrls,
  withMainImageSlots,
} from "../../utils/productMainImages";

const PRODUCT_HELP_VIDEO_URL = "https://youtu.be/15y6Uqg4L3A?si=DEO_LuZ2g7DRSG0y";

// File objects لا تُحفظ في sessionStorage — نحتفظ بها في الذاكرة أثناء الانتقال لإنشاء براند/قسم
const pendingProductImageFiles = {
  mainImage: null,
  mainImages: [],
  variantImages: {},
};

const cacheProductFormFiles = (form) => {
  const slots = getFormMainImageSlots(form);
  pendingProductImageFiles.mainImages = slots.map((slot) => slot?.file || null);
  pendingProductImageFiles.mainImage = pendingProductImageFiles.mainImages[0] || null;
  pendingProductImageFiles.variantImages = {};
  (form?.variants || []).forEach((v, vidx) => {
    (v.colorGroups || []).forEach((cg, cgIdx) => {
      if (cg?.image?.file) {
        pendingProductImageFiles.variantImages[`${vidx}-${cgIdx}`] = cg.image.file;
      }
    });
  });
};

const clearCachedProductFormFiles = () => {
  pendingProductImageFiles.mainImage = null;
  pendingProductImageFiles.mainImages = [];
  pendingProductImageFiles.variantImages = {};
};

const reattachProductFormFiles = (form) => {
  if (!form) return form;

  const next = { ...form };
  const slots = getFormMainImageSlots(next).map((slot, i) => {
    const cached = pendingProductImageFiles.mainImages?.[i] || (i === 0 ? pendingProductImageFiles.mainImage : null);
    if (cached instanceof File) {
      return {
        ...(slot || emptyMainImageSlot()),
        file: cached,
        isChanged: true,
        existingUrl:
          slot?.existingUrl && String(slot.existingUrl).startsWith("blob:")
            ? slot.existingUrl
            : URL.createObjectURL(cached),
      };
    }
    if (!slot) return emptyMainImageSlot();
    return {
      ...slot,
      file: null,
      isChanged: Boolean(slot.isChanged && slot.existingUrl),
    };
  });

  Object.assign(next, withMainImageSlots(next, slots));

  next.variants = (next.variants || []).map((v, vidx) => ({
    ...v,
    colorGroups: (v.colorGroups || []).map((cg, cgIdx) => {
      const cached = pendingProductImageFiles.variantImages[`${vidx}-${cgIdx}`];
      if (cached instanceof File) {
        return {
          ...cg,
          image: {
            ...(cg.image || {}),
            file: cached,
            isChanged: true,
            existingUrl:
              cg.image?.existingUrl && String(cg.image.existingUrl).startsWith("blob:")
                ? cg.image.existingUrl
                : URL.createObjectURL(cached),
          },
        };
      }
      if (!cg?.image) return cg;
      return {
        ...cg,
        image: {
          ...cg.image,
          file: null,
        },
      };
    }),
  }));

  return next;
};



export default function AdminAllProducts() {
  const navigate = useNavigate();
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const getItemId = (item) =>
    item?.itemId ??
    item?.ItemId ??
    item?.id ??
    null;

  const calculatePriceAfterDiscount = (originalPrice, discountValue, discountType) => {
    const price = parseFloat(originalPrice) || 0;
    const discount = parseFloat(discountValue) || 0;
    let finalPrice = price;

    if (discountType === 1) { // Percentage %
      finalPrice = price - (price * (discount / 100));
    } else { // Fixed Amount
      finalPrice = price - discount;
    }
    return finalPrice;
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [stockMap, setStockMap] = useState(new Map()); // Store stock data
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  const baseImageUrl = API_BASE.replace("/api", "");

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    Promise.all([
      // ===== TAGS =====
      fetch(`${API_BASE}/Tags/get?tenantId=${tenantId}`, {
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((r) => r.json()),

      // ===== CATEGORIES =====
      fetch(`${API_BASE}/TenantCategory/GetCategories?TenantId=${tenantId}`, {
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((r) => r.json()),
    ])
      .then(([tagsData, categoriesData]) => {
        // ===== NORMALIZE TAGS =====
        const normalizedTags = Array.isArray(tagsData)
          ? tagsData
            .filter(t => t.isActive === true || t.isActive === 1)
            .map((t) => ({
              id: t.tagId,
              name: t.tagName,
            }))
          : [];


        // ===== NORMALIZE CATEGORIES =====
        // داخل useEffect الخاص بـ Categories
        // داخل useEffect الخاص بـ Categories
        const normalizedCategories = Array.isArray(categoriesData)
          ? categoriesData
            .filter(c => c.isActive === true || c.isActive === 1)
            .map((c) => ({
              id: c.categoryId,
              name: c.categoryName,
              // تأكد من الـ Key القادم من الـ API (قد يكون SubCategories أو subCategories)
              subCategories: Array.isArray(c.subCategories || c.SubCategories)
                ? (c.subCategories || c.SubCategories).map((sc) => ({
                  id: sc.subCategoryId || sc.id, // جرب الاحتمالين
                  name: sc.subCategoryName || sc.name,
                }))
                : [],
            }))
          : [];
        setCategories(normalizedCategories);

        setTags(normalizedTags);
      })
      .catch((err) => {
        console.error("Fetch Tags/Categories Error:", err);
        setTags([]);
        setCategories([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [API_BASE, tenantId]);

  const [rawItems, setRawItems] = useState([]);

  // ==== SEARCH ====
  const [query, setQuery] = useState("");
  const filteredProducts = useMemo(() => {
    // If we let ResponsiveTable handle search, we can just return products here
    // But since the user complained search is not working, I'll make sure it's linked
    return products;
  }, [products]);


  // ==== MODAL ====
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [current, setCurrent] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const createEmptyColorGroup = () => ({
    color: { id: 0, name: "", colorName: "", isSelected: false },
    image: { file: null, existingUrl: null, isChanged: false },
    // variantId: 0 = حجم جديد (الـ API ينشئ Variant جديد)
    sizes: [{ variantId: 0, size: "", quantity: "", lowStockValue: "" }]
  });
  const [form, setForm] = useState({
    name: "",
    itemCode: "",
    tagsId: 0,
    categoryId: 0,
    subCategoryId: 0,
    description: "",
    isActive: true,
    isSingleProduct: false, // New field
    variants: [
      {
        variantName: "",
        price: "",
        costPrice: "",
        discount: { type: 0, value: 0 },
        lowStockValue: 0,
        colorGroups: [createEmptyColorGroup()]
      }
    ],
    mainImage: emptyMainImageSlot(),
    mainImages: [emptyMainImageSlot()],
    discountValue: 0,
    discountType: 0, // 0 for Fixed, 1 for Percentage
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedVariant, setExpandedVariant] = useState(0); // Track expanded variant index

  const handleOpenMovement = (targetItemId) => {
    navigate(`/admin-store/products/movement/${targetItemId}`);
  };


  const getCategoryId = (item) =>
    item?.category?.id ??
    item?.categoryId ??
    0;

  const mapApiItemToProduct = (item, stockData = null) => {
    let variantsRaw = item?.variants ?? item?.Variants ?? [];
    if (typeof variantsRaw === "string") {
      try {
        variantsRaw = JSON.parse(variantsRaw);
      } catch {
        variantsRaw = [];
      }
    }
    const variantsFromApi = Array.isArray(variantsRaw) ? variantsRaw : [];

    // Ensure itemStockData is always an array
    let itemStockData = [];
    if (Array.isArray(stockData)) {
      itemStockData = stockData;
    } else if (stockData && typeof stockData === 'object' && stockData !== null) {
      itemStockData = [stockData];
    } else {
      const fromMap = stockMap.get(item.itemId ?? item.ItemId);
      itemStockData = Array.isArray(fromMap) ? fromMap : (fromMap ? [fromMap] : []);
    }

    // Get stock for each variant by variantId
    const processedVariants = variantsFromApi.map(v => {
      const variantId = v.variantId ?? v.VariantId;
      // Find matching stock entry by variantId or SerialNumber if applicable
      const vStock = (Array.isArray(itemStockData) ? itemStockData : [])
        .find(s => (s.variantId || s.VariantId || s.id || s.Id) === variantId) || {};
      return normalizeVariant(v, vStock);
    });

    // Find images and sort them
    const images = [...processedVariants]
      .sort((a, b) => (Number(b.stock?.quantity) || 0) - (Number(a.stock?.quantity) || 0))
      .map(v => v.imageUrl || v.image?.existingUrl || null)
      .filter((img, idx, self) => img && self.indexOf(img) === idx);

    // Quantity in table: variants[].stock.quantity from GetItems
    const totalQty = variantsFromApi.reduce((sum, v) => {
      const stockObj = v.stock || v.Stock;
      const q = Number(stockObj?.quantity ?? stockObj?.Quantity);
      if (Number.isFinite(q)) return sum + q;
      return sum;
    }, 0);

    const prices = processedVariants
      .map(v => Number(v.price))
      .filter(n => Number.isFinite(n));

    const costPrices = processedVariants
      .map(v => Number(v.costPrice))
      .filter(n => Number.isFinite(n));

    const minPrice = prices.length ? Math.min(...prices) : 0;
    const maxPrice = prices.length ? Math.max(...prices) : 0;

    const minCost = costPrices.length ? Math.min(...costPrices) : 0;
    const maxCost = costPrices.length ? Math.max(...costPrices) : 0;

    const catId = getCategoryId(item);
    const categoryName =
      categories.find(c => c.id === catId)?.name ||
      item?.category?.name ||
      "—";

    const mainImages = extractMainImageUrls(item);

    return {
      itemId: item.itemId ?? item.ItemId,
      name: item.itemName || item.ItemName || "",
      itemCode: item.itemCode || item.ItemCode || "",
      description: item.description || item.Description || "",
      isActive: item.isActive ?? item.IsActive ?? true,
      categoryId: catId,
      category: categoryName,
      subCategoryId:
        item.subCategory?.subCategoryId ??
        item.subCategory?.id ??
        item.SubCategoryId ??
        0,
      subCategory:
        item.subCategory?.subCategoryName ??
        item.subCategory?.name ??
        item.SubCategoryName ??
        "—",
      tagsId: item.tags?.tagId ?? item.tags?.id ?? item.TagsId ?? 0,
      type: item.tags?.tagName ?? item.tags?.name ?? item.TagName ?? "—",
      orders: item.ordersCount ?? item.OrdersCount ?? 0,
      quantity: totalQty,
      priceMin: minPrice,
      priceMax: maxPrice,
      costPriceMin: minCost,
      costPriceMax: maxCost,
      stockStatus: item.stockStatus || item.StockStatus || (totalQty > 0 ? "AVAILABLE" : "OUT_OF_STOCK"),
      variants: processedVariants,
      variantCount: processedVariants.length,
      variantImages: images,
      mainImages,
      mainImage: mainImages[0] || item.mainImageUrl || item.MainImageUrl || item.mainImage,
      discountValue: item.discountValue ?? item.DiscountValue ?? 0,
      discountType: item.discountType ?? item.DiscountType ?? 0,
    };
  };

  const urlToFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      // نحدد النوع بناءً على البلوب أو نفترضه image/jpeg
      return new File([blob], fileName, { type: blob.type || "image/jpeg" });
    } catch (error) {
      console.error("Failed to convert URL to File:", error);
      return null;
    }
  };

  const buildImageUrl = (imagePath) => {
    if (!imagePath || imagePath === "" || imagePath === "null") {
      return "https://placehold.co/120x120/e0e0e0/666?text=Product";
    }

    // For local blob previews (newly uploaded images)
    if (imagePath.startsWith("blob:")) return imagePath;

    // إذا كان الرابط كامل يبدأ بـ http لا نغيره
    if (imagePath.startsWith("http")) return imagePath;

    // تنظيف المسار من أي سلاش زائد في البداية
    let cleanPath = imagePath.startsWith("/") ? imagePath.substring(1) : imagePath;

    // إذا كان المسار يحتوي أصلاً على كلمة uploads، ندمجه مباشرة مع الـ BaseURL
    if (cleanPath.startsWith("uploads/")) {
      return `${baseImageUrl}/${cleanPath}`;
    }

    // الحالة الاحتياطية
    return `${baseImageUrl}/uploads/${tenantId}/items/${cleanPath.split('/').pop()}`;
  };

  const getProductDisplayImage = (product) =>
    (product?.mainImages || [])[0] || product?.mainImage || product?.mainImageUrl || (product?.variantImages || [])[0] || null;
  // داخل الـ .then الخاص بـ GetItems

  const resolveSizeVariantId = (sizeObj, parentVariant, sizesInParent = 1) => {
    const fromSize = Number(sizeObj?.variantId ?? sizeObj?.VariantId ?? 0);
    if (fromSize > 0) return fromSize;
    // عند صف API واحد = حجم واحد، الـ variantId يكون على الـ variant الأب
    if (sizesInParent <= 1) {
      const fromParent = Number(parentVariant?.variantId ?? parentVariant?.VariantId ?? 0);
      if (fromParent > 0) return fromParent;
    }
    return 0;
  };

  const pushUniqueFormSize = (sizes, sizeObj) => {
    const id = Number(sizeObj.variantId || 0);
    const sizeKey = String(sizeObj.size || "").trim().toLowerCase();

    // نفس variantId مسبقاً
    if (id > 0 && sizes.some((s) => Number(s.variantId) === id)) return;

    // نفس اسم الحجم (مكررات من تحديثات سابقة خاطئة) — نحتفظ بالأقدم/الأصغر id
    const sameSizeIdx = sizeKey
      ? sizes.findIndex((s) => String(s.size || "").trim().toLowerCase() === sizeKey)
      : -1;
    if (sameSizeIdx >= 0) {
      const existingId = Number(sizes[sameSizeIdx].variantId || 0);
      if (id > 0 && (existingId === 0 || id < existingId)) {
        sizes[sameSizeIdx] = sizeObj;
      }
      return;
    }

    sizes.push(sizeObj);
  };

  const normalizeVariant = (v, vStock = {}) => {
    const vStockData = vStock || {};

    // استخراج الألوان بشكل دقيق من الـ API
    // لاحظ هنا نأخذ hexCode و name كما يظهر في الـ Response
    const colorObj = v.color || v.Color || vStockData.color || null;

    const hexCode = String(colorObj?.hexCode || colorObj?.HexCode || "").trim();
    const colorName = String(colorObj?.name || colorObj?.Name || "").trim();
    const hasColor = Boolean(hexCode || colorName);
    const colorInfo = hasColor ? {
      id: colorObj.id || colorObj.Id || 0,
      name: hexCode, // Internal 'name' is Hex for color picker
      colorName,     // Internal 'colorName' is Text Name
      isSelected: true
    } : { id: 0, name: "", colorName: "", isSelected: false };

    // الكمية على مستوى الـ variant من stock.quantity؛ أحجام sizes تحتفظ بـ stockQty لكل سايز
    const stockObj = v.stock || v.Stock || vStockData;
    let quantity = Number(stockObj?.quantity ?? stockObj?.Quantity);
    let lowStockVal = Number(
      stockObj?.lowStockValue ??
      stockObj?.LowStockValue ??
      stockObj?.reorderPoint ??
      stockObj?.ReorderPoint ??
      vStockData?.lowStockValue ??
      vStockData?.LowStockValue ??
      vStockData?.reorderPoint ??
      vStockData?.ReorderPoint
    );
    const rawSizes = Array.isArray(v.sizes)
      ? v.sizes
      : (Array.isArray(v.Sizes) ? v.Sizes : []);

    if (!Number.isFinite(quantity)) {
      if (rawSizes.length > 0) {
        quantity = rawSizes.reduce((sum, s) => sum + (s.stockQty || s.StockQty || s.quantity || s.Quantity || 0), 0);
      } else {
        quantity = v.stockQty || v.StockQty || v.quantity || v.Quantity || 0;
      }
    }
    if (!Number.isFinite(lowStockVal)) {
      lowStockVal = Number(
        rawSizes[0]?.lowStockValue ??
        rawSizes[0]?.LowStockValue ??
        rawSizes[0]?.reorderPoint ??
        rawSizes[0]?.ReorderPoint ??
        v.lowStockValue ??
        v.LowStockValue ??
        0
      );
    }
    if (!Number.isFinite(lowStockVal)) lowStockVal = 0;

    const parentVariantId = Number(v.variantId ?? v.VariantId ?? 0);
    const normalizedSizes = rawSizes.map((s) => {
      const sizeStock = s.stock || s.Stock || {};
      const sizeLow = Number(
        s.lowStockValue ??
        s.LowStockValue ??
        sizeStock.lowStockValue ??
        sizeStock.LowStockValue ??
        s.reorderPoint ??
        s.ReorderPoint ??
        sizeStock.reorderPoint ??
        sizeStock.ReorderPoint
      );
      return {
        variantId: resolveSizeVariantId(s, v, rawSizes.length),
        size: s.size || s.Size || "",
        quantity: s.stockQty || s.StockQty || s.quantity || s.Quantity || sizeStock.quantity || sizeStock.Quantity || 0,
        lowStockValue: Number.isFinite(sizeLow) ? sizeLow : lowStockVal,
      };
    });

    // memory منفصل عن sizes — لا نستبدله باسم الحجم
    const memoryObj = v.memory || v.Memory || null;

    return {
      variantId: parentVariantId,
      variantName: v.variantName || v.VariantName || "",
      color: colorInfo,
      memory: memoryObj
        ? { id: Number(memoryObj.id || memoryObj.Id || 0), name: memoryObj.name || memoryObj.Name || "" }
        : null,
      price: v.price || v.Price || 0,
      costPrice: v.costPrice || v.CostPrice || 0,
      stock: {
        quantity: quantity,
        status: v.stockStatus || v.Stock?.status || (quantity > 0 ? "AVAILABLE" : "OUT_OF_STOCK"),
        lowStockValue: lowStockVal,
      },
      sizes: normalizedSizes,
      imageUrl: v.imageUrl || v.ImageUrl || null,
    };
  };
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setForm((f) => {
        const slots = [...getFormMainImageSlots(f)];
        slots[0] = { file, existingUrl: url, isChanged: true };
        return withMainImageSlots(f, slots);
      });
      if (fieldErrors.mainImage) {
        setFieldErrors((prev) => ({ ...prev, mainImage: false }));
      }
    }
  };
  const updateColorHex = (vidx, cgIdx, hex) => {
    setForm((f) => {
      const newVariants = [...f.variants];
      const newV = { ...newVariants[vidx] };
      newV.colorGroups = [...newV.colorGroups];
      newV.colorGroups[cgIdx] = {
        ...newV.colorGroups[cgIdx],
        color: { ...newV.colorGroups[cgIdx].color, name: hex, isSelected: true },
      };
      newVariants[vidx] = newV;
      return { ...f, variants: newVariants };
    });
  }; const updateColorName = (vidx, cgIdx, name) => {
    setForm((f) => {
      const newVariants = [...f.variants];
      const newV = { ...newVariants[vidx] };
      newV.colorGroups = [...newV.colorGroups];
      newV.colorGroups[cgIdx] = {
        ...newV.colorGroups[cgIdx],
        color: { ...newV.colorGroups[cgIdx].color, colorName: name },
      };
      newVariants[vidx] = newV;
      return { ...f, variants: newVariants };
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (!tenantId || tenantId <= 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    Promise.all([
      // 1. Get Items
      fetch(`${API_BASE}/GetItems?tenantId=${tenantId}&itemId=-1`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then(async (res) => {
        if (!res.ok) {
          console.error(`GetItems failed with status: ${res.status} for tenant: ${tenantId}`);
          throw new Error(`Failed to fetch products (Status: ${res.status})`);
        }
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      }),

      // 2. Get Stock (for correct quantities)
      fetch(`${API_BASE}/ItemsStock?tenantId=${tenantId}&itemId=-1`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
    ])
      .then(([itemsData, stockData]) => {
        // تحضير بيانات المخزون في Map
        const sMap = new Map();
        stockData.forEach(s => {
          const id = s.itemId || s.ItemId;
          if (!sMap.has(id)) sMap.set(id, []);
          sMap.get(id).push(s);
        });

        setStockMap(sMap);
        setRawItems(itemsData);

        // ✅ الحل هنا: تصفية المنتجات النشطة فقط وعمل الـ Map داخل الـ then
        const activeItems = itemsData.filter(item => item.isActive !== false);
        setProducts(activeItems.map(item => mapApiItemToProduct(item, sMap.get(item.itemId))));
      })
      .catch((err) => {
        console.error("Fetch products/stock error:", err);
        setRawItems([]);
        setProducts([]);
      })
      .finally(() => {
        setLoading(false);
      });

  }, [API_BASE, tenantId]);

  useEffect(() => {
    if (!rawItems.length) return;
    // Re-map if categories change, using existing stockMap
    setProducts(rawItems.map(item => mapApiItemToProduct(item, stockMap.get(item.itemId))));
  }, [rawItems, categories, stockMap]);

  // Restore form state after navigating back from Brand/Category pages
  useEffect(() => {
    const saved = sessionStorage.getItem("all_products_form_state");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        const restoredForm = reattachProductFormFiles(data.form);
        setForm(restoredForm);
        setModalMode(data.modalMode);
        setCurrent(data.current);
        setExpandedVariant(data.expandedVariant);
        setModalOpen(data.modalOpen);
        sessionStorage.removeItem("all_products_form_state");
        // نُبقي الكاش حتى نجاح الحفظ تحسباً لأي إعادة رندر
      } catch (e) {
        console.error("Error restoring form state:", e);
        sessionStorage.removeItem("all_products_form_state");
        clearCachedProductFormFiles();
      }
    }
  }, []);



  useEffect(() => {
    if (modalOpen || zoomImage) {
      document.body.style.overflow = 'hidden';
      document.body.style.pointerEvents = 'auto';
    } else {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.pointerEvents = '';
    };
  }, [modalOpen, zoomImage]);

  // Page-level permission guard (after all hooks)
  if (!perms.viewProducts) {
    return <NoAccess />;
  }

  const totalProducts = products.length;
  const availableProducts = products.filter((p) => p.quantity > 0).length;

  const cards = [
    {
      title: isArabic ? "عدد كل المنتجات" : "Total Products",
      value: totalProducts,
    },
    {
      title: isArabic ? "المنتجات المتوفّرة" : "Available Products",
      value: availableProducts,
    },
  ];

  const openModal = async (mode, item = null) => {
    setModalMode(mode);
    setCurrent(item);
    setExpandedVariant(0); // Open first variant
    setFieldErrors({});

    if (mode === "create") {
      setForm({
        name: "",
        itemCode: "",
        tagsId: 0,
        categoryId: 0,
        subCategoryId: 0,
        description: "",
        isActive: true,
        isSingleProduct: false,
          variants: [
            {
              variantName: "",
              price: "",
              costPrice: "",
              discount: { type: 0, value: 0 },
              lowStockValue: 0,
              colorGroups: [createEmptyColorGroup()]
            }
          ],
        mainImage: emptyMainImageSlot(),
        mainImages: [emptyMainImageSlot()],
        discountValue: 0,
        discountType: 0,
      });
    } else if (item) {
      const data = mapApiItemToProduct(item);

      if (mode === "view") {
        const viewMainImages = extractMainImageUrls(item);
        setCurrent({
          ...item,
          ...data,
          itemName: item.itemName || item.ItemName || data.name || "",
          variants: data.variants,
          mainImages: viewMainImages.length ? viewMainImages : data.mainImages,
          mainImage: (viewMainImages[0] || data.mainImage),
        });
      } else {
        // Edit mode - Populate Form
        // Group variants by base details -> then by Color+Memory -> then unique sizes
        const grouped = [];
        data.variants.forEach(v => {
          const memoryKey = `${v.memory?.id || 0}|${v.memory?.name || ""}`;
          const baseKey = `${v.variantName}-${v.price}-${v.costPrice || ''}-${v.discount?.type}-${v.discount?.value}-${memoryKey}`;
          let existingBase = grouped.find(g => g._baseKey === baseKey);

          if (!existingBase) {
            existingBase = {
              _baseKey: baseKey,
              variantName: v.variantName,
              price: v.price,
              costPrice: v.costPrice || "",
              lowStockValue: 0,
              colorGroups: []
            };
            grouped.push(existingBase);
          }

          const colorKey = `${v.color?.name || ""}|${v.color?.colorName || ""}|${memoryKey}`;
          let existingColor = existingBase.colorGroups.find(cg => cg._colorKey === colorKey);

          if (!existingColor) {
            existingColor = {
              _colorKey: colorKey,
              color: {
                id: v.color?.id || 0,
                name: v.color?.name || "",
                colorName: v.color?.colorName || "",
                isSelected: !!(v.color?.name || v.color?.colorName)
              },
              memory: v.memory
                ? { id: Number(v.memory.id || 0), name: v.memory.name || "" }
                : null,
              image: { file: null, existingUrl: v.imageUrl, isChanged: false },
              discount: { type: v.discount?.type || 0, value: v.discount?.value || 0 },
              sizes: []
            };
            existingBase.colorGroups.push(existingColor);
          }

          // أحجام موجودة: variantId من الـ API | حجم جديد لاحقاً من الواجهة: 0
          if (Array.isArray(v.sizes) && v.sizes.length > 0) {
            v.sizes.forEach((sz) => {
              pushUniqueFormSize(existingColor.sizes, {
                variantId: resolveSizeVariantId(sz, v, v.sizes.length),
                size: sz.size || "",
                quantity: sz.quantity ?? 0,
                lowStockValue: sz.lowStockValue ?? 0
              });
            });
          } else {
            const fallbackSize =
              (typeof v.size === "string" ? v.size : v.size?.name) || "";
            if (fallbackSize || Number(v.variantId || 0) > 0) {
              pushUniqueFormSize(existingColor.sizes, {
                variantId: Number(v.variantId || 0),
                size: fallbackSize,
                quantity: v.stock?.quantity || v.quantity || 0,
                lowStockValue: v.stock?.lowStockValue || 0
              });
            }
          }
        });

        const finalVariants = grouped.map(({ _baseKey, ...g }) => ({
          ...g,
          colorGroups: g.colorGroups.map(({ _colorKey, ...cg }) => cg)
        }));

        const mainImageUrls = extractMainImageUrls(item);
        setForm({
          name: data.name,
          itemCode: data.itemCode,
          tagsId: data.tagsId,
          categoryId: data.categoryId,
          subCategoryId: data.subCategoryId,
          description: data.description,
          isActive: data.isActive,
          isSingleProduct: data.categoryId === 0 && data.tagsId === 0,
          variants: finalVariants.length > 0 ? finalVariants : [
            {
              variantName: "",
              price: "",
              costPrice: "",
              discount: { type: 0, value: 0 },
              lowStockValue: 0,
              colorGroups: [createEmptyColorGroup()]
            }
          ],
          mainImages: slotsFromImageUrls(mainImageUrls.length ? mainImageUrls : data.mainImages),
          mainImage: {
            file: null,
            existingUrl: mainImageUrls[0] || data.mainImage || null,
            isChanged: false,
          },
          discountValue: data.discountValue || 0,
          discountType: data.discountType || 0
        });
      }
    }
    setModalOpen(true);
  };


  const closeModal = () => {
    clearCachedProductFormFiles();
    setModalOpen(false);
  };



  const buildTenantItemFormData = async ({ mode, current }) => {
    const fd = new FormData();
    const itemId = mode === "edit" ? (current?.itemId || current?.ItemId || current?.id) : "";

    const generatedItemCode = itemId ? `${form.name.trim()}-${itemId}` : form.name.trim();

    // 1. البيانات الأساسية
    fd.append("TenantId", tenantId);
    fd.append("ItemName", form.name.trim());
    fd.append("ItemCode", generatedItemCode); fd.append("TagsId", form.tagsId || 0);
    fd.append("CategoryId", form.categoryId || 0);
    fd.append("SubCategoryId", form.subCategoryId || 0);
    fd.append("Description", form.description || "");
    fd.append("IsActive", form.isActive);
    fd.append("DiscountValue", Number(form.discountValue || 0));
    fd.append("DiscountType", Number(form.discountType || 0));

    if (mode === "edit" && current) {
      const itemId = current.itemId || current.ItemId || current.id;
      fd.append("ItemId", itemId);
    }

    // 2. معالجة الصور الرئيسية (حتى 3 صور) — نفس أسلوب الإضافة: ملفات فقط داخل MainImages
    const mainSlots = getFormMainImageSlots(form)
      .slice(0, MAX_MAIN_IMAGES)
      .filter((slot) => slot?.file instanceof File || slot?.existingUrl);

    const mainImageFiles = [];

    for (let i = 0; i < mainSlots.length; i++) {
      const slot = mainSlots[i];
      let file = slot.file instanceof File ? slot.file : null;
      const existing = String(slot.existingUrl || "");
      if (!file && existing && !existing.startsWith("blob:") && !existing.startsWith("data:")) {
        file = await urlToFile(buildImageUrl(existing), `main_image_${i + 1}.jpg`);
      }
      if (file) {
        mainImageFiles.push(file);
        fd.append(`MainImages[${i}]`, file);
      }
    }

    const mainImageFile = mainImageFiles[0] || null;

    let imageCounter = 0;
    const variantImageFiles = [];
    const mappedVariants = [];

    // 3. تحويل المتغيرات (Variants) مع معالجة الصور كملفات دايماً
    for (let vidx = 0; vidx < form.variants.length; vidx++) {
      const gv = form.variants[vidx];
      const price = Number(gv.price || 0);
      const costPrice = Number(gv.costPrice || 0);

      for (let cgIdx = 0; cgIdx < gv.colorGroups.length; cgIdx++) {
        const cg = gv.colorGroups[cgIdx];
        let currentImageKey = null;

        // منطق الصور: تحويل الرابط لملف إذا لم تكن صورة جديدة
        const variantFile = cg.image?.file instanceof File ? cg.image.file : null;
        if (variantFile && (cg.image.isChanged || mode === "create")) {
          // حالة صورة جديدة مرفوعة
          const key = imageCounter.toString();
          variantImageFiles.push({
            file: variantFile,
            index: key
          });
          currentImageKey = key;
          imageCounter++;
        } else if (cg.image?.existingUrl) {
          // حالة صورة قديمة: نحولها لملف ونبعثها كأنها مرفوعة الآن
          const existingFile = await urlToFile(buildImageUrl(cg.image.existingUrl), `existing_v_${imageCounter}.jpg`);

          if (existingFile) {
            const key = imageCounter.toString();
            variantImageFiles.push({
              file: existingFile,
              index: key
            });
            currentImageKey = key;
            imageCounter++;
          }
        } else {
          // إذا لم يتم توفير صورة فرعية، نستخدم الصورة الرئيسية كبديل
          if (mainImageFile) {
            const key = imageCounter.toString();
            variantImageFiles.push({
              file: mainImageFile,
              index: key
            });
            currentImageKey = key;
            imageCounter++;
          } else if (mainSlots[0]?.existingUrl) {
            const existingFile = await urlToFile(buildImageUrl(mainSlots[0].existingUrl), `fallback_v_${imageCounter}.jpg`);
            if (existingFile) {
              const key = imageCounter.toString();
              variantImageFiles.push({
                file: existingFile,
                index: key
              });
              currentImageKey = key;
              imageCounter++;
            }
          }
        }

        // صنف واحد لكل colorGroup، وكل الأحجام داخل sizes مع variantId لكل حجم
        // موجود → variantId من الـ API | جديد → 0
        const hasSelectedColor = !!(cg.color?.isSelected || cg.color?.colorName?.trim());
        const colorPayload = hasSelectedColor ? {
          id: Number(cg.color?.id || 0),
          name: cg.color?.colorName || cg.color?.name || "Color",
          hexCode: cg.color?.name || "#ffffff"
        } : null;

        // memory الحقيقي فقط — لا نرسل اسم الحجم كـ memory
        const sizeNames = new Set(
          (cg.sizes || []).map((s) => String(s.size || "").trim().toLowerCase()).filter(Boolean)
        );
        const memoryName = String(cg.memory?.name || "").trim();
        const memoryPayload =
          memoryName && !sizeNames.has(memoryName.toLowerCase())
            ? { id: Number(cg.memory?.id || 0), name: memoryName }
            : null;

        const existingImageUrl =
          !cg.image?.isChanged && cg.image?.existingUrl && !currentImageKey
            ? cg.image.existingUrl
            : null;

        const seenIds = new Set();
        const seenSizeNames = new Set();
        const sizesPayload = [];
        const sizeRows = cg.sizes || [];

        sizeRows.forEach((sz) => {
          const sizeLabel = String(sz.size || "").trim();
          if (!sizeLabel) return;

          const variantId = Number(sz.variantId || 0);
          const sizeKey = sizeLabel.toLowerCase();

          if (variantId > 0) {
            if (seenIds.has(variantId)) return;
            seenIds.add(variantId);
          }
          // منع تكرار نفس اسم الحجم (من بيانات مكررة سابقاً)
          if (seenSizeNames.has(sizeKey)) return;
          seenSizeNames.add(sizeKey);

          sizesPayload.push({
            variantId, // موجود من الـ API | جديد = 0
            size: sizeLabel,
            stockQty: Number(sz.quantity || 0),
            lowStockType: 1,
            lowStockValue: Number(sz.lowStockValue || 0)
          });
        });

        if (sizesPayload.length === 0) return;

        mappedVariants.push({
          variantName: gv.variantName,
          color: colorPayload,
          memory: memoryPayload,
          price,
          costPrice,
          sizes: sizesPayload,
          imageKey: currentImageKey,
          imageUrl: existingImageUrl
        });
      }
    }

    // 4. إرسال المصفوفة كـ JSON
    fd.append("Variants", JSON.stringify(mappedVariants));

    // 5. إرفاق جميع الملفات (الجديدة والقديمة المحولة)
    variantImageFiles.forEach((imgObj) => {
      fd.append(`VariantImages[${imgObj.index}]`, imgObj.file);
    });

    return fd;
  };

  const handleGoToBrands = () => {
    cacheProductFormFiles(form);
    sessionStorage.setItem("all_products_form_state", JSON.stringify({
      form: {
        ...form,
        mainImage: {
          ...(form.mainImage || {}),
          file: null, // File لا يُسلسل — يُستعاد من الكاش
        },
        mainImages: getFormMainImageSlots(form).map((slot) => ({
          ...(slot || emptyMainImageSlot()),
          file: null,
        })),
        variants: (form.variants || []).map((v) => ({
          ...v,
          colorGroups: (v.colorGroups || []).map((cg) => ({
            ...cg,
            image: cg.image ? { ...cg.image, file: null } : cg.image,
          })),
        })),
      },
      modalMode,
      current,
      expandedVariant,
      modalOpen: true
    }));
    navigate("/admin-store/categories", { state: { openAdd: true, returnTo: "/admin-store/products" } });
  };

  const handleGoToCategories = () => {
    cacheProductFormFiles(form);
    sessionStorage.setItem("all_products_form_state", JSON.stringify({
      form: {
        ...form,
        mainImage: {
          ...(form.mainImage || {}),
          file: null,
        },
        mainImages: getFormMainImageSlots(form).map((slot) => ({
          ...(slot || emptyMainImageSlot()),
          file: null,
        })),
        variants: (form.variants || []).map((v) => ({
          ...v,
          colorGroups: (v.colorGroups || []).map((cg) => ({
            ...cg,
            image: cg.image ? { ...cg.image, file: null } : cg.image,
          })),
        })),
      },
      modalMode,
      current,
      expandedVariant,
      modalOpen: true
    }));
    navigate("/admin-store/divisions", { state: { openAdd: true, returnTo: "/admin-store/products" } });
  };


  const handleSave = async (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    const errors = {};
    if (!form.name?.trim()) errors.name = true;

    const hasMainImage = getFormMainImageSlots(form).some((slot) => slot?.file || slot?.existingUrl);
    if (!hasMainImage) {
      errors.mainImage = true;
    }

    if (!form.isSingleProduct) {
      if (!form.tagsId || form.tagsId === 0) errors.tagsId = true;
      if (!form.categoryId || form.categoryId === 0) errors.categoryId = true;
    }

    if (!form.variants || form.variants.length === 0) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خصائص المنتج مطلوبة" : "Product properties required",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    // Validate Variants Basic Fields
    form.variants.forEach((v, vidx) => {
      if (!v.variantName) errors[`variants[${vidx}].variantName`] = true;
      if (!v.price) errors[`variants[${vidx}].price`] = true;
      if (!v.costPrice) errors[`variants[${vidx}].costPrice`] = true;
      v.colorGroups.forEach((cg, cgIdx) => {
        cg.sizes.forEach((sz, szIdx) => {
          // الحجم والكمية ونقص المخزون إلزامية — الكمية ونقص المخزون ≥ 0
          if (!String(sz.size || "").trim()) {
            errors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].size`] = true;
          }
          const qtyNum = Number(sz.quantity);
          if (
            sz.quantity === "" ||
            sz.quantity === null ||
            sz.quantity === undefined ||
            !Number.isFinite(qtyNum) ||
            qtyNum < 0
          ) {
            errors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].quantity`] = true;
          }
          const lowNum = Number(sz.lowStockValue);
          if (
            sz.lowStockValue === "" ||
            sz.lowStockValue === null ||
            sz.lowStockValue === undefined ||
            !Number.isFinite(lowNum) ||
            lowNum < 0
          ) {
            errors[`variants[${vidx}].colorGroups[${cgIdx}].sizes[${szIdx}].lowStockValue`] = true;
          }
        });
      });
    });

    // --- إضافة: التحقق من أن السعر بعد الخصم ليس صفراً أو أقل ---
    let hasInvalidPrice = false;
    let hasLossPrice = false;
    let problematicVariant = "";

    form.variants.forEach((v) => {
      const price = parseFloat(v.price) || 0;
      const cost = parseFloat(v.costPrice) || 0;
      const discVal = parseFloat(form.discountValue) || 0;

      // حساب السعر النهائي باستخدام الدالة الموجودة مسبقاً في الكود
      const finalPrice = calculatePriceAfterDiscount(price, discVal, form.discountType);

      if (finalPrice <= 0 && price > 0) {
        hasInvalidPrice = true;
      }

      // الشرط الجديد: إذا كان السعر النهائي أقل من التكلفة
      if (finalPrice < cost) {
        hasLossPrice = true;
        problematicVariant = v.variantName || (isArabic ? "إحدى الخصائص" : "one of the properties");
      }
    });

    if (hasInvalidPrice) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ في السعر النهائي" : "Invalid Final Price",
        text: isArabic
          ? "قيمة الخصم تجعل سعر المنتج صفراً أو أقل!"
          : "Discount value makes the price zero or less!",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    // تنبيه الخسارة
    if (hasLossPrice) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "تنبيه: خسارة محتملة" : "Warning: Potential Loss",
        text: isArabic
          ? `سعر البيع بعد الخصم للخاصية (${problematicVariant}) أقل من سعر التكلفة! لا يمكن البيع بخسارة.`
          : `Final price for (${problematicVariant}) is lower than the cost price! You cannot sell at a loss.`,
        confirmButtonColor: colors.mainColor,
      });
      return;
    }
    // -------------------------------------------------------

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ في البيانات" : "Validation Error",
        text: isArabic ? "يرجى ملء جميع الحقول المطلوبة (باللون الأحمر)" : "Please fill all required fields (highlighted in red)",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
    // For edit mode, use UpdateItems API
    if (modalMode === "edit" && current) {
      try {
        const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
        const token = localStorage.getItem("authToken");

        const fd = await buildTenantItemFormData({
          mode: "edit",
          current,
        });

        const res = await fetch(
          `${API_BASE}/UpdateItems/UpdateTenantItem`,
          {
            method: "POST",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
            body: fd,
          }
        );

        let data = {};
        const ct = res.headers.get("content-type");
        if (ct && ct.includes("application/json")) {
          data = await res.json();
        } else {
          const t = await res.text();
          try {
            data = t ? JSON.parse(t) : {};
          } catch {
            data = { message: t };
          }
        }

        if (!res.ok) {
          const apiErrors = {};
          if (data?.errors) {
            Object.keys(data.errors).forEach(key => {
              if (key.toLowerCase().includes("name")) apiErrors.name = true;
              if (key.toLowerCase().includes("category")) apiErrors.categoryId = true;
              if (key.toLowerCase().includes("tags")) apiErrors.tagsId = true;
            });
            setFieldErrors(apiErrors);
          }

          let errorMessage = data?.message || data?.error || data?.Message || (isArabic ? "فشل تحديث المنتج ❌" : "Failed to update product ❌");
          Swal.fire({
            icon: "error",
            title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
            text: errorMessage,
            confirmButtonColor: colors.mainColor,
          });
          return;
        }

        Swal.fire({
          icon: "success",
          title: isArabic ? "تم التعديل! ✅" : "Updated! ✅",
          confirmButtonColor: colors.mainColor,
          timer: 2000,
          showConfirmButton: false,
        });

        closeModal();

        setLoading(true);
        const itemId = getItemId(current);
        const refetch = await fetch(
          `${API_BASE}/GetItems?tenantId=${tenantId}&itemId=${itemId}`,
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );

        if (refetch.ok) {
          const [freshItem] = await refetch.json();
          setRawItems(prev => prev.map(it => it.itemId === itemId ? freshItem : it));
          setProducts(prev => prev.map(p => p.itemId === itemId ? mapApiItemToProduct(freshItem) : p));
        }
        setLoading(false);

      } catch (err) {
        Swal.fire({
          icon: "error",
          title: isArabic ? "فشل التحديث ❌" : "Update Failed ❌",
          text: err.message,
          confirmButtonColor: colors.mainColor,
        });
      }
      return;
    }

    if (modalMode === "create") {
      try {
        const token = localStorage.getItem("authToken");
        const fd = await buildTenantItemFormData({ mode: "create" });

        const res = await fetch(
          `${API_BASE}/TenantsItems/AddTenantItem`,
          {
            method: "POST",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
            body: fd,
          }
        );

        let data = {};
        const ct = res.headers.get("content-type");
        if (ct && ct.includes("application/json")) {
          data = await res.json();
        } else {
          const t = await res.text();
          data = t ? JSON.parse(t) : {};
        }

        if (!res.ok) throw new Error(data?.message || "Add failed");

        Swal.fire({
          icon: "success",
          title: isArabic ? "تمت الإضافة ✅" : "Added ✅",
          timer: 1500,
          showConfirmButton: false,
        });

        closeModal();

        setLoading(true);
        const r = await fetch(`${API_BASE}/GetItems?tenantId=${tenantId}&itemId=-1`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (r.ok) {
          const list = await r.json();
          setRawItems(list);
          setProducts(list.map(mapApiItemToProduct));
        }
        setLoading(false);
      } catch (e) {
        Swal.fire({ icon: "error", title: "Error", text: e.message });
      }
      return;
    }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const itemId = getItemId(item);
    const tenantId = Number(localStorage.getItem("tenantId")) || -1;
    const token = localStorage.getItem("authToken");

    // 🔴 Validation قبل أي شيء
    if (!itemId || itemId <= 0 || tenantId <= 0) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ في البيانات" : "Invalid Data",
        text: isArabic
          ? "رقم المنتج أو التاجر غير صحيح"
          : "Invalid itemId or tenantId",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
      text: isArabic
        ? `سيتم حذف المنتج "${item.name}" نهائيًا`
        : `Product "${item.name}" will be permanently deleted`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#7c3aed", // Fixed Purple to ensure visibility
      cancelButtonColor: "#ef4444", // Red for Cancel
      confirmButtonText: isArabic ? "نعم، احذف" : "Yes, delete",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE}/DeleteItem?tenantId=${tenantId}&itemId=${itemId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "*/*",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { message: text };
      }

      if (!res.ok) {
        throw new Error(
          data?.message ||
          (isArabic ? "فشل حذف المنتج" : "Failed to delete product")
        );
      }

      // ✅ تحديث الـ state بشكل صحيح
      setProducts((prev) =>
        prev.filter((p) => getItemId(p) !== itemId)
      );

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف ✅" : "Deleted ✅",
        confirmButtonColor: colors.mainColor,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ" : "Error",
        text: err.message,
        confirmButtonColor: colors.mainColor,
      });
    }
  };


  // ==== UI ====
  if (loading) {
    return (
      <PageContainer title={isArabic ? "كل المنتجات" : "All Products"}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-400">{isArabic ? "جاري التحميل..." : "Loading..."}</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={isArabic ? "كل المنتجات" : "All Products"}>
      {/* ===== العنوان + الإحصاءات ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c, i) => (
          <UnifiedCard
            key={i}
            icon={FiBox}
            title={c.title}
            value={c.value}
            valueColor={colors.gold}
            statCard={true}
          />
        ))}
      </div>

      {/* ===== البحث + زر الإضافة ===== */}


      {/* ===== الجدول ===== */}
      <div className="w-full">
        <ResponsiveTable
          columns={[
            { header: isArabic ? "صورة" : "Image", align: "center" },
            { header: isArabic ? "الاسم" : "Name", sortKey: "name", align: "center" },
            { header: isArabic ? "البراند" : "Brand", sortKey: "type", align: "center" }, // Renamed from Type to Brand
            { header: isArabic ? "القسم" : "Category", sortKey: "category", align: "center" },
            { header: isArabic ? "السعر" : "Price", align: "center" },
            { header: isArabic ? "التكلفة" : "Cost", align: "center" },
            { header: isArabic ? "الكمية" : "Qty", align: "center" },
            { header: isArabic ? "عدد الطلبات" : "Orders", align: "center" },
            { header: isArabic ? "تحكم" : "Actions", align: "center" },
          ]}

          data={products}
          searchable={true}
          searchFields={["name", "category", "type", "subCategory"]}
          sortable={true}
          headerActions={
            <div className="flex items-center gap-2 w-full md:w-auto">
              <a
                href={PRODUCT_HELP_VIDEO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium active:scale-[1.02] transition-transform shrink-0 border"
                style={{
                  borderColor: colors.border,
                  color: colors.text,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#fff",
                }}
              >
                <FiHelpCircle size={16} />
                {isArabic ? "مساعدة" : "Help"}
              </a>
              {perms.createProduct && (
                <button
                  onClick={() => openModal("create")}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium active:scale-[1.02] transition-transform flex-1 md:flex-none"
                  style={{
                    backgroundColor: colors.mainColor,
                    boxShadow: "0 4px 12px rgba(116,0,191,0.25)",
                  }}
                >
                  <FiPlus />
                  {isArabic ? "منتج جديد" : "Add Product"}
                </button>
              )}
            </div>
          }
          filterOptions={{
            field: "category",
            options: [
              { value: "", label: isArabic ? "الكل" : "All" },
              ...Array.from(new Set(products.map((p) => p.category).filter(Boolean))).map((cat) => ({
                value: cat,
                label: cat,
              })),
            ],
          }}
          renderRow={(p) => (
            <>
              <td className="py-3 px-4 text-center">
                <div className="flex items-center justify-center">
                  <img
                    src={buildImageUrl(getProductDisplayImage(p))}
                    className="w-10 h-10 object-cover rounded cursor-pointer"
                    onClick={() => setZoomImage(buildImageUrl(getProductDisplayImage(p)))}
                  />
                </div>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                {(() => {
                  const name = p.name || "—";
                  if (name === "—") return <span className="font-medium text-sm sm:text-base whitespace-nowrap">—</span>;
                  const words = name.split(" ");
                  if (words.length <= 2) return <span className="font-medium text-sm sm:text-base whitespace-nowrap">{name}</span>;
                  return (
                    <div className="flex flex-col items-center justify-center">
                      <span className="font-medium text-sm sm:text-base whitespace-nowrap">{words.slice(0, 2).join(" ")}</span>
                      <span className="text-xs mt-1 whitespace-normal break-words max-w-[180px]" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>{words.slice(2).join(" ")}</span>
                    </div>
                  );
                })()}
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{renderText(p.type)}


                </span>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{p.category || "—"}</span>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex flex-col items-center justify-center whitespace-nowrap">
                  <span className="font-semibold text-sm sm:text-base" style={{ color: colors.gold }}>
                    {p.priceMin === p.priceMax
                      ? `${p.priceMin}`
                      : `${p.priceMin} - ${p.priceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </span>
                </div>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex flex-col items-center justify-center whitespace-nowrap">
                  <span className="font-semibold text-sm sm:text-base" style={{ color: colors.red }}>
                    {p.costPriceMin === p.costPriceMax
                      ? `${p.costPriceMin}`
                      : `${p.costPriceMin} - ${p.costPriceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </span>
                </div>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: (p.quantity === 0 || p.stockStatus === "LOW_STOCK" || p.stockStatus === "OUT_OF_STOCK") ? colors.red : (isDark ? "#f1f5f9" : "#111827") }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{p.quantity || 0}</span>
              </td>
              <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                <span className="text-sm sm:text-base whitespace-nowrap">{p.orders || 0}</span>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex justify-center items-center gap-2 flex-nowrap whitespace-nowrap">
                  <button
                    onClick={() => {
                      const fullItem = rawItems.find(r => r.itemId === p.itemId);
                      if (!fullItem) return;
                      openModal("view", fullItem);

                    }}


                    className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm flex-shrink-0"
                    style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                    title={isArabic ? "عرض" : "View"}
                  >
                    <FiEye size={16} />
                  </button>
                  {perms.editProduct && (
                    <button
                      onClick={async () => {
                        const token = localStorage.getItem("authToken");
                        const res = await fetch(
                          `${API_BASE}/GetItems?tenantId=${tenantId}&itemId=${p.itemId}`,
                          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
                        );

                        if (!res.ok) return;

                        const payload = await res.json();
                        const fullItem = Array.isArray(payload) ? payload[0] : payload;
                        if (fullItem) openModal("edit", fullItem);
                      }}


                      className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm flex-shrink-0"
                      style={{ background: colors.mainColor }}
                      title={isArabic ? "تعديل" : "Edit"}
                    >
                      <FiEdit2 size={16} />
                    </button>
                  )}
                  {perms.deleteProduct && (
                    <button
                      onClick={() => handleDelete(p)}
                      className="p-2 sm:p-2.5 rounded-lg text-white transition-all active:scale-105 shadow-sm flex-shrink-0"
                      style={{ background: colors.red }}
                      title={isArabic ? "حذف" : "Delete"}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </td>
            </>
          )}
          renderCard={(p) => (
            <div className="space-y-3">
              <div className="flex gap-4 items-start">
                <img
                  src={buildImageUrl(getProductDisplayImage(p))}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://placehold.co/120x120/e0e0e0/666?text=Product";
                  }}
                />

                <div className="flex-1">
                  <h3 className="font-bold text-lg" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {(() => {
                      const name = p.name || "—";
                      if (name === "—") return name;
                      const words = name.split(" ");
                      if (words.length <= 2) return name;
                      return (
                        <div className="flex flex-col">
                          <span>{words.slice(0, 2).join(" ")}</span>
                          <span className="text-sm font-normal mt-0.5" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>{words.slice(2).join(" ")}</span>
                        </div>
                      );
                    })()}
                  </h3>
                  <div className="flex gap-2 mt-1 text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    <span>{renderText(p.type)}</span>                <span>•</span>
                    <span>{p.category}</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb" }}>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "السعر:" : "Price:"}
                  </p>
                  <p className="font-bold" style={{ color: colors.gold }}>
                    {p.priceMin === p.priceMax
                      ? p.priceMin
                      : `${p.priceMin} - ${p.priceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "التكلفة:" : "Cost:"}
                  </p>
                  <p className="font-bold" style={{ color: colors.red }}>
                    {p.costPriceMin === p.costPriceMax
                      ? p.costPriceMin
                      : `${p.costPriceMin} - ${p.costPriceMax}`
                    } {isArabic ? "د.ع" : "IQD"}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "عدد الطلبات:" : "Orders:"}
                  </p>
                  <p className="font-bold" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    {p.orders || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    {isArabic ? "الكمية:" : "Qty:"}
                  </p>
                  <p className="font-bold" style={{ color: (p.quantity === 0 || p.stockStatus === "LOW_STOCK" || p.stockStatus === "OUT_OF_STOCK") ? colors.red : (isDark ? "#f1f5f9" : "#111827") }}>
                    {p.quantity || 0}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 pt-2 flex-nowrap overflow-x-auto">
                <button
                  onClick={() => {
                    const fullItem = rawItems.find(r => r.itemId === p.itemId);
                    openModal("view", fullItem);
                  }}

                  className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm"
                  style={{ background: colors.gold, color: isDark ? "#1e293b" : "#ffffff" }}
                >
                  <FiEye className="inline mr-1" size={14} />
                  <span className="whitespace-nowrap">{isArabic ? "عرض" : "View"}</span>
                </button>
                {perms.editProduct && (
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("authToken");
                      const res = await fetch(
                        `${API_BASE}/GetItems?tenantId=${tenantId}&itemId=${p.itemId}`,
                        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
                      );
                      if (res.ok) {
                        const payload = await res.json();
                        const fullItem = Array.isArray(payload) ? payload[0] : payload;
                        if (fullItem) {
                          openModal("edit", fullItem);
                          return;
                        }
                      }
                      const fallback = rawItems.find((r) => r.itemId === p.itemId);
                      openModal("edit", fallback || p);
                    }}
                    className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm"
                    style={{ background: colors.mainColor }}
                  >
                    <FiEdit2 className="inline mr-1" size={14} />
                    <span className="whitespace-nowrap">{isArabic ? "تعديل" : "Edit"}</span>
                  </button>
                )}
                {perms.deleteProduct && (
                  <button
                    onClick={() => handleDelete(p)}
                    className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all active:scale-105 shadow-sm"
                    style={{ background: colors.red }}
                  >
                    <FiTrash2 className="inline mr-1" size={14} />
                    <span className="whitespace-nowrap">{isArabic ? "حذف" : "Delete"}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        />
      </div>

      {/* ===== المودال ===== */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{
              pointerEvents: modalOpen ? "auto" : "none",
              zIndex: modalOpen ? 9999 : "unset",
              background: isDark ? "rgba(2,6,23,0.72)" : "rgba(15,23,42,0.45)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
            }}
            onClick={closeModal}
          >
            <motion.div
              initial={{ y: 40, scale: 0.96, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 24, scale: 0.98, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="w-full max-w-3xl rounded-t-2xl sm:rounded-2xl shadow-2xl relative flex flex-col"
              style={{
                backgroundColor: colors.cardBg,
                border: `1px solid ${colors.border}`,
                maxHeight: "90vh",
                color: colors.text,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="shrink-0 z-[100] flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-b"
                style={{
                  borderColor: colors.border,
                  background: isDark
                    ? "linear-gradient(180deg, rgba(255,255,255,0.03), transparent)"
                    : "linear-gradient(180deg, rgba(106,17,203,0.04), transparent)",
                }}
              >
                <div className="min-w-0 flex items-center gap-2">
                  <div
                    className="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center shrink-0"
                    style={{
                      backgroundColor: `${colors.mainColor}18`,
                      color: colors.mainColor,
                    }}
                  >
                    <FiPackage size={16} />
                  </div>
                  <div className="min-w-0">
                    <h2
                      className="text-sm sm:text-base font-bold truncate"
                      style={{ color: colors.text }}
                    >
                      {modalMode === "view"
                        ? (isArabic ? "عرض المنتج" : "View Product")
                        : modalMode === "edit"
                          ? (isArabic ? "تعديل المنتج" : "Edit Product")
                          : (isArabic ? "إضافة منتج جديد" : "Add New Product")}
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {modalMode === "view" && (
                    <button
                      onClick={() => handleOpenMovement(getItemId(current))}
                      className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg font-bold transition-all active:scale-105 shadow-sm flex items-center gap-1.5 text-xs"
                      style={{ backgroundColor: colors.gold, color: "#1e293b" }}
                      title={isArabic ? "حركة المادة" : "Product Movement"}
                    >
                      <FiActivity size={15} />
                      <span className="hidden sm:inline">{isArabic ? "حركة المادة" : "Movement"}</span>
                    </button>
                  )}
                  {modalMode === "create" && (
                    <a
                      href={PRODUCT_HELP_VIDEO_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-[0.98] flex items-center gap-1.5 border"
                      style={{
                        borderColor: colors.border,
                        color: colors.text,
                        backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#fff",
                      }}
                    >
                      <FiHelpCircle size={14} />
                      {isArabic ? "مساعدة" : "Help"}
                    </a>
                  )}
                  {modalMode !== "view" && (
                    <button
                      type="button"
                      disabled={isSaving}
                      aria-busy={isSaving}
                      onClick={handleSave}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-[0.98] shadow-sm flex items-center gap-1.5 disabled:opacity-70 disabled:cursor-wait"
                      style={{
                        backgroundColor: colors.mainColor,
                        color: isDark ? "#1e293b" : "#fff",
                        pointerEvents: isSaving ? "none" : "auto",
                        cursor: isSaving ? "wait" : "pointer",
                      }}
                    >
                      <FiCheck size={14} />
                      <span className="sm:hidden">{isSaving ? (isArabic ? "جاري..." : "...") : (isArabic ? "حفظ" : "Save")}</span>
                      <span className="hidden sm:inline">
                        {isSaving
                          ? (isArabic ? "جاري الحفظ..." : "Saving...")
                          : modalMode === "edit"
                            ? (isArabic ? "حفظ التغييرات" : "Save Changes")
                            : (isArabic ? "حفظ المنتج" : "Save Product")}
                      </span>
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200"
                    style={{
                      color: colors.subText,
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#EF4444";
                      e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.subText;
                      e.currentTarget.style.backgroundColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    <FiX size={18} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="product-modal-scroll px-3 sm:px-4 py-2.5 sm:py-3 flex-1 overflow-y-auto overscroll-contain">

                {modalMode === "view" && current && (
                  (() => {
                    const currentTag = tags.find(t =>
                      t.id === (current.tags?.tagId ?? current.tags?.id ?? current.tagsId)
                    );

                    const currentCategory = categories.find(c =>
                      c.id === (current.category?.categoryId ?? current.category?.id ?? current.categoryId)
                    );

                    const mainImgs = extractMainImageUrls(current);
                    const mainImg = mainImgs[0];
                    const galleryImages = [...(current.variants || [])]
                      .sort((a, b) => (Number(b.stock?.quantity) || 0) - (Number(a.stock?.quantity) || 0))
                      .flatMap(v => [v.imageUrl || mainImg])
                      .filter((img, idx, self) => img && self.indexOf(img) === idx);

                    const groupedForView = [];
                    (current.variants || []).forEach(v => {
                      const key = `${v.variantName}-${v.price}-${v.color?.name}`;
                      let g = groupedForView.find(x => x._key === key);
                      if (!g) {
                        g = { _key: key, ...v, allSizes: [] };
                        g.stock = { ...v.stock, quantity: Number(v.stock?.quantity || 0) };
                        if (v.sizes && v.sizes.length > 0) {
                          g.allSizes = v.sizes.map((sz) => ({
                            ...sz,
                            size: sz.size || sz.Size || "—",
                            quantity: Number(sz.quantity ?? sz.stockQty ?? sz.StockQty ?? 0),
                            lowStockValue: Number(
                              sz.lowStockValue ??
                              sz.LowStockValue ??
                              v.stock?.lowStockValue ??
                              0
                            ),
                          }));
                        } else {
                          g.allSizes.push({
                            size: v.memory?.name || v.size?.name || "—",
                            quantity: v.stock?.quantity || 0,
                            lowStockValue: Number(v.stock?.lowStockValue ?? v.lowStockValue ?? 0),
                          });
                        }
                        groupedForView.push(g);
                      } else {
                        if (v.sizes && v.sizes.length > 0) {
                          g.allSizes.push(
                            ...v.sizes.map((sz) => ({
                              ...sz,
                              size: sz.size || sz.Size || "—",
                              quantity: Number(sz.quantity ?? sz.stockQty ?? sz.StockQty ?? 0),
                              lowStockValue: Number(
                                sz.lowStockValue ??
                                sz.LowStockValue ??
                                v.stock?.lowStockValue ??
                                0
                              ),
                            }))
                          );
                        }
                        if (g.stock) g.stock.quantity += Number(v.stock?.quantity || 0);
                      }
                    });

                    const infoItems = [
                      {
                        label: isArabic ? "النوع" : "Type",
                        value: currentTag?.name || "—",
                        icon: <FiPackage size={14} />,
                      },
                      {
                        label: isArabic ? "القسم" : "Category",
                        value: currentCategory?.name || "—",
                        icon: <FiTag size={14} />,
                      },
                      {
                        label: isArabic ? "الخصم العام" : "Global Discount",
                        value: current.discountValue
                          ? `${current.discountValue} ${current.discountType === 1 ? "%" : (isArabic ? "د.ع" : "IQD")}`
                          : "—",
                        icon: <FiTag size={14} />,
                      },
                      {
                        label: isArabic ? "عدد الطلبات" : "Orders",
                        value: current.orders || 0,
                        icon: <FiBox size={14} />,
                      },
                    ];

                    return (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.22 }}
                        className="space-y-3"
                      >
                        {/* Hero: image + title */}
                        <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-3 items-start">
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => mainImg && setZoomImage(buildImageUrl(mainImg))}
                              className="relative w-full aspect-square max-h-[150px] rounded-xl border overflow-hidden group cursor-pointer"
                              style={{
                                borderColor: colors.border,
                                backgroundColor: isDark ? "#0f172a" : "#fff",
                              }}
                            >
                              <img
                                src={buildImageUrl(mainImg)}
                                className="w-full h-full object-contain p-2"
                                alt="Main product"
                              />
                              <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <FiEye className="text-white" size={18} />
                              </div>
                            </button>
                            {mainImgs.length > 1 && (
                              <div className="flex gap-1.5">
                                {mainImgs.map((img, imgIdx) => (
                                  <button
                                    key={imgIdx}
                                    type="button"
                                    onClick={() => setZoomImage(buildImageUrl(img))}
                                    className="w-10 h-10 rounded-lg border overflow-hidden"
                                    style={{
                                      borderColor: colors.border,
                                      backgroundColor: isDark ? "#0f172a" : "#fff",
                                    }}
                                  >
                                    <img src={buildImageUrl(img)} className="w-full h-full object-contain p-0.5" alt="" />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 space-y-2">
                            <div>
                              <h3
                                className="text-base sm:text-lg font-bold leading-snug"
                                style={{ color: colors.text }}
                              >
                                {current.itemName}
                              </h3>
                              {(current.description || current.itemDescription) && (
                                <p
                                  className="text-[12px] mt-1 leading-relaxed"
                                  style={{ color: colors.subText }}
                                >
                                  {current.description || current.itemDescription || ""}
                                </p>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-1.5">
                              {infoItems.map((item) => (
                                <div
                                  key={item.label}
                                  className="rounded-lg border px-2 py-1.5 flex items-center gap-2 min-w-0"
                                  style={{
                                    borderColor: colors.border,
                                    backgroundColor: isDark
                                      ? "rgba(255,255,255,0.03)"
                                      : "rgba(248,250,252,0.9)",
                                  }}
                                >
                                  <div
                                    className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                                    style={{
                                      backgroundColor: `${colors.mainColor}15`,
                                      color: colors.mainColor,
                                    }}
                                  >
                                    {item.icon}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[9px] font-medium truncate" style={{ color: colors.subText }}>
                                      {item.label}
                                    </div>
                                    <div className="text-[12px] font-bold truncate" style={{ color: colors.text }}>
                                      {item.value}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Gallery */}
                        {galleryImages.length > 0 && (
                          <div>
                            <p className="text-[11px] font-bold mb-1.5" style={{ color: colors.subText }}>
                              {isArabic ? "صور الخصائص" : "Property Images"}
                            </p>
                            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                              {galleryImages.map((img, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setZoomImage(buildImageUrl(img))}
                                  className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-lg border overflow-hidden shrink-0 group"
                                  style={{
                                    borderColor: colors.border,
                                    backgroundColor: isDark ? "#0f172a" : "#fff",
                                  }}
                                >
                                  <img
                                    src={buildImageUrl(img)}
                                    className="w-full h-full object-contain p-1"
                                    alt=""
                                  />
                                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Variants */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[11px] font-bold" style={{ color: colors.subText }}>
                              {isArabic ? "تفاصيل الخصائص" : "Property Details"}
                            </p>
                            <span
                              className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: `${colors.mainColor}15`,
                                color: colors.mainColor,
                              }}
                            >
                              {groupedForView.length} {isArabic ? (groupedForView.length === 1 ? "خاصية" : "خصائص") : (groupedForView.length === 1 ? "property" : "properties")}
                            </span>
                          </div>

                          <div className="space-y-2">
                            {groupedForView.map((v, idx) => {
                              const finalPrice = calculatePriceAfterDiscount(
                                v.price,
                                current.discountValue,
                                current.discountType
                              );
                              const hasDiscount = current.discountValue > 0;
                              const variantDisplayImage =
                                v.imageUrl || current.mainImageUrl || current.mainImage;

                              return (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, y: 6 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: idx * 0.04, duration: 0.18 }}
                                  className="rounded-xl border p-2.5"
                                  style={{
                                    borderColor: colors.border,
                                    backgroundColor: isDark
                                      ? "rgba(15,23,42,0.55)"
                                      : "#fff",
                                  }}
                                >
                                  <div className="flex gap-2.5 items-start">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        variantDisplayImage &&
                                        setZoomImage(buildImageUrl(variantDisplayImage))
                                      }
                                      className="w-14 h-14 rounded-lg border overflow-hidden shrink-0"
                                      style={{
                                        borderColor: colors.border,
                                        backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                                        cursor: variantDisplayImage ? "pointer" : "default",
                                      }}
                                    >
                                      <img
                                        src={buildImageUrl(variantDisplayImage)}
                                        className="w-full h-full object-contain p-1"
                                        alt=""
                                      />
                                    </button>

                                    <div className="flex-1 min-w-0 space-y-1.5">
                                      <div className="flex flex-wrap items-start justify-between gap-1.5">
                                        <div className="min-w-0">
                                          <div className="text-[13px] font-bold truncate" style={{ color: colors.text }}>
                                            {v.variantName || `${isArabic ? "خاصية" : "Property"} ${idx + 1}`}
                                          </div>
                                          <div className="flex items-center gap-1.5 mt-0.5 text-[11px]" style={{ color: colors.subText }}>
                                            <span
                                              className="inline-block w-3 h-3 rounded-full border shrink-0"
                                              style={{
                                                background: v.color?.name || "transparent",
                                                backgroundImage: v.color?.name
                                                  ? undefined
                                                  : "linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)",
                                                backgroundSize: v.color?.name ? undefined : "6px 6px",
                                                backgroundPosition: v.color?.name
                                                  ? undefined
                                                  : "0 0, 0 3px, 3px -3px, -3px 0",
                                                borderColor: colors.border,
                                              }}
                                            />
                                            <span className="truncate">
                                              {isArabic ? "اللون" : "Color"}
                                              {v.color?.colorName ? `: ${v.color.colorName}` : ""}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="text-end shrink-0">
                                          {hasDiscount ? (
                                            <>
                                              <div className="text-[13px] font-bold" style={{ color: colors.gold }}>
                                                {finalPrice.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                              </div>
                                              <div className="text-[10px] line-through opacity-45">
                                                {v.price.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                              </div>
                                            </>
                                          ) : (
                                            <div className="text-[13px] font-bold" style={{ color: colors.gold }}>
                                              {v.price.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      <div
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md"
                                        style={{
                                          backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                                          color: colors.subText,
                                        }}
                                      >
                                        <FiBox size={11} />
                                        {isArabic ? "الكمية الكلية" : "Total Qty"}:{" "}
                                        <span style={{ color: colors.text }}>{v.stock?.quantity ?? 0}</span>
                                      </div>

                                      <div
                                        className="rounded-lg p-1.5"
                                        style={{
                                          backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                        }}
                                      >
                                        <span className="text-[9px] font-bold uppercase tracking-wide opacity-50 block mb-1">
                                          {isArabic ? "الأحجام والكميات ونقص المخزون" : "Sizes, Qty & Low Stock"}
                                        </span>
                                        <div className="flex flex-wrap gap-1.5">
                                          {v.allSizes.map((sz, szIdx) => {
                                            const qty = Number(sz.quantity ?? sz.stockQty ?? 0) || 0;
                                            const low = Number(
                                              sz.lowStockValue ??
                                              sz.LowStockValue ??
                                              v.stock?.lowStockValue ??
                                              0
                                            ) || 0;
                                            const isAtLow = low > 0 && qty > 0 && qty <= low;
                                            return (
                                              <span
                                                key={szIdx}
                                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px]"
                                                style={{
                                                  borderColor: isAtLow
                                                    ? "rgba(245, 158, 11, 0.35)"
                                                    : colors.border,
                                                  backgroundColor: isAtLow
                                                    ? (isDark ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.08)")
                                                    : (isDark ? "#1e293b" : "#fff"),
                                                  color: colors.text,
                                                }}
                                                title={isArabic ? "الحجم · الكمية · نقص المخزون" : "Size · Qty · Low stock"}
                                              >
                                                <span className="font-bold">{sz.size || "—"}</span>
                                                <span className="opacity-30">·</span>
                                                <span
                                                  className="font-semibold"
                                                  style={{
                                                    color: qty === 0 ? "#EF4444" : isAtLow ? "#D97706" : colors.green,
                                                  }}
                                                >
                                                  {qty}
                                                </span>
                                                <span className="opacity-30">·</span>
                                                <span
                                                  className="inline-flex items-center gap-0.5 font-semibold tabular-nums"
                                                  style={{ color: isAtLow ? "#D97706" : colors.subText }}
                                                >
                                                  {low}
                                                </span>
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })()
                )}

                {(modalMode === "create" || modalMode === "edit") && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (isSaving) return;
                      handleSave(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.target.type !== "submit") {
                        const focusableElements = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';
                        const elements = Array.from(e.currentTarget.querySelectorAll(focusableElements));
                        const index = elements.indexOf(e.target);
                        if (index > -1 && index < elements.length - 1) {
                          e.preventDefault();
                          elements[index + 1].focus();
                        }
                      }
                    }}
                    className="space-y-1"
                  >
                    <ProductFormFields
                      form={form}
                      setForm={setForm}
                      fieldErrors={fieldErrors}
                      setFieldErrors={setFieldErrors}
                      tags={tags}
                      categories={categories}
                      expandedVariant={expandedVariant}
                      setExpandedVariant={setExpandedVariant}
                      createEmptyColorGroup={createEmptyColorGroup}
                      buildImageUrl={buildImageUrl}
                      calculatePriceAfterDiscount={calculatePriceAfterDiscount}
                      updateColorHex={updateColorHex}
                      updateColorName={updateColorName}
                      handleGoToBrands={handleGoToBrands}
                      handleGoToCategories={handleGoToCategories}
                      isArabic={isArabic}
                      isDark={isDark}
                      colors={colors}
                    />
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Zoom Image Modal */}
      <AnimatePresence>
        {zoomImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            style={{
              pointerEvents: zoomImage ? 'auto' : 'none',
              zIndex: zoomImage ? 10000 : 'unset',
              background: 'transparent',
            }}
            onClick={() => setZoomImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoomImage}
                alt="Zoomed"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                style={{
                  backgroundColor: "#ffffff",
                  padding: "12px",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
                }}
              />
              <button
                onClick={() => setZoomImage(null)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full text-white bg-black/50 active:bg-black/70 transition-all duration-200 backdrop-blur-sm"
                style={{
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                }}
              >
                <FiX size={24} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </PageContainer>
  );
}
const renderText = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number") return v;
  if (typeof v === "object") return v.name ?? "—";
  return "—";
};


/* ==== Input + InfoTile ==== */
const TextInput = ({ label, value, onChange, type = "text", isDark, error, placeholder }) => {
  const { colors } = useUI();

  const handleInputChange = (e) => {
    let val = e.target.value;
    if (type === "number") {
      // Allow only numbers and one decimal point
      val = val.replace(/[^0-9.]/g, '');
      const parts = val.split('.');
      if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
    }
    onChange(val);
  };

  return (
    <label className="block">
      <span className="text-sm font-semibold block mb-2" style={{ color: error ? "#EF4444" : colors.text }}>
        {label}
      </span>
      <input
        type={type === "number" ? "text" : type}
        inputMode={type === "number" ? "decimal" : undefined}
        value={value || ""}
        placeholder={placeholder}
        onChange={handleInputChange}
        onWheel={(e) => e.target.blur()}
        className="w-full rounded-lg px-3 py-2 outline-none transition-all duration-200 focus:ring-2 resize-none"
        style={{
          backgroundColor: isDark ? "#1e293b" : "#f8fafc",
          border: `1px solid ${error ? "#EF4444" : (isDark ? "#334155" : "#e2e8f0")}`,
          color: isDark ? "#f1f5f9" : "#111827",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = colors.inputFocus;
          e.target.style.boxShadow = `0 0 0 2px ${colors.inputFocus}20`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? "#EF4444" : colors.inputBorder;
          e.target.style.boxShadow = "none";
        }}
      />
    </label>
  );
}

function InfoTile({ label, value, icon }) {
  const { colors } = useUI();
  return (
    <div
      className="rounded-lg border p-3 flex items-center gap-3 transition-all duration-200"
      style={{
        borderColor: colors.border,
        backgroundColor: colors.tableHeaderBg,
      }}
    >
      <div className="text-xl" style={{ color: colors.mainColor }}>{icon}</div>
      <div>
        <div className="text-xs" style={{ color: colors.subText }}>{label}</div>
        <div className="font-semibold" style={{ color: colors.text }}>{value}</div>
      </div>
    </div>
  );
}
